import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import Switch from '@/components/Switch/Switch';
import { toast } from '@/components/toast';
import CopyIcon from '@/images/Copy.svg';
import GlobeIcon from '@/images/Global.svg';
import { useGetAgents } from '@/pages/agents/api';
import { useGetPublicInsights } from '@/pages/agents/api/insights';
import { useAgentInsightsContext } from '@/pages/agents/pages/insights/contex/useAgentInsightsContext';
import { ALL_AGENTS } from '@/pages/agents/pages/insights/types';
import { createPublicInsights, deletePublicInsights } from '@/services/api/did-api/insights-api';
import theme from '@/theme';
import { copyToClipboard } from '@/utils/shareUtils';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Dialog, Skeleton, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 20px;
        display: flex;
        flex-direction: column;
        width: 600px;
        padding: 24px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
        gap: 24px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    color: ${theme.colors.secondary[900]};
`;

const CloseButton = styled(IconButton)`
    color: ${theme.colors.black[800]};
    padding: 4px;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StyledAdornmentSelect = styled(AdornmentSelect<IAgent | null>)`
    width: 150px;

    & .MuiInputBase-root {
        height: 40px;
    }

    & .MuiInputBase-input {
        font-size: 14px;
        cursor: pointer;
        width: auto !important;
        padding: 8px 12px;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 8px;
        border-color: ${theme.colors.black[300]};
    }
`;

const ShareLinkSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ShareLinkHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const ShareLinkLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LabelText = styled(Typography)`
    font-size: 16px;
    font-weight: 500;
    color: ${theme.colors.secondary[900]};
`;

const ShareLinkText = styled(Typography)`
    font-size: 16px;
    font-weight: 500;
    color: ${theme.colors.secondary[900]};
`;

const LinkInputContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    margin-top: 16px;
`;

const StyledTextField = styled(TextField)`
    flex: 1;

    & .MuiInputBase-root {
        background: ${theme.colors.black[50]};
        border-radius: 8px;
        font-size: 14px;
        color: ${theme.colors.secondary[900]};
        height: 40px;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.black[300]};
    }

    &:hover .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.black[400]};
    }
`;

const CopyButton = styled(IconButton)`
    padding: 8px 16px;
    background: ${theme.colors.secondary[900]};
    border-radius: 8px;
    min-width: 100px;
    height: 40px;
    color: white;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;

    &:hover {
        background: ${theme.colors.secondary[800]};
    }

    &.MuiButtonBase-root {
        border: none;
    }
`;

const CopyButtonText = styled.span`
    color: white;
    font-size: 14px;
    font-weight: 600;
`;

const ShareLinkSkeleton = styled(Skeleton)`
    border-radius: 8px;
    height: 24px;
    width: 50px;
`;

interface ShareDashboardModalProps {
    onClose: () => void;
}

const generateShareUrl = (hashId: string): string => {
    return `${window.location.protocol}//${window.location.host}/insights/share?id=${hashId}`;
};

export const ShareDashboardModal = ({ onClose }: ShareDashboardModalProps) => {
    const { data } = useGetAgents(false);
    const { agent: contextAgent } = useAgentInsightsContext();
    const queryClient = useQueryClient();
    const agents = data?.items.filter(a => a.access !== 'public') || [];

    const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(() => {
        if (agents.length === 0) return null;

        // If "All Agents" is selected in insights, select the first agent
        if (contextAgent.id === ALL_AGENTS.id || !contextAgent.id) {
            return agents[0];
        }

        // Otherwise, use the currently selected agent from insights
        const agentFromContext = agents.find(a => a.id === contextAgent.id);
        return agentFromContext || agents[0];
    });

    const [isTogglingLink, setIsTogglingLink] = useState(false);

    // Fetch existing public insights link
    const { data: existingLink, isLoading: isFetchingLink } = useGetPublicInsights(selectedAgent?.id);
    const isShareLinkEnabled = !!(existingLink && existingLink.shareId);
    const shareUrl = existingLink?.shareId ? generateShareUrl(existingLink.shareId) : '';

    const handleAgentChange = (agent: IAgent | null) => {
        setSelectedAgent(agent);
    };

    const handleToggleShareLink = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        if (!selectedAgent) {
            toast.error({
                title: 'Please select an agent first',
                toastId: 'select-agent-error',
            });
            return;
        }

        setIsTogglingLink(true);

        try {
            if (isChecked) {
                await createPublicInsights(selectedAgent.id);
                toast.success({
                    title: 'Share link created successfully',
                    toastId: 'share-link-created',
                });
            } else {
                await deletePublicInsights(selectedAgent.id);
                toast.success({
                    title: 'Share link deleted successfully',
                    toastId: 'share-link-deleted',
                });
            }
            // Refetch the public insights to update the UI
            await queryClient.refetchQueries(['public-insights', selectedAgent.id]);
        } catch (error) {
            toast.error({
                title: isChecked ? 'Failed to create share link' : 'Failed to delete share link',
                toastId: 'share-link-error',
            });
        } finally {
            setIsTogglingLink(false);
        }
    };

    const handleCopyLink = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (shareUrl) {
            copyToClipboard(shareUrl);
        }
    };

    return (
        <StyledDialog open onClose={onClose}>
            <Header>
                <Title>Share dashboard</Title>
                <CloseButton ariaLabel="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </Header>

            <Content>
                <Section>
                    <LabelText>Agent</LabelText>
                    <StyledAdornmentSelect
                        id="share-agent-select"
                        label=""
                        options={agents}
                        getOptionLabel={option => option?.preview_name || ''}
                        value={selectedAgent}
                        onChange={(_, value) => handleAgentChange(value)}
                        placeholder="Select an agent"
                        className="options-select"
                        optionSx={{ fontWeight: 400 }}
                        paperSx={{ borderRadius: '8px' }}
                    />
                </Section>

                <ShareLinkSection>
                    <ShareLinkHeader>
                        <ShareLinkLabel>
                            <MaskedIcon src={GlobeIcon} size="16px" color={theme.colors.secondary[900]} />
                            <ShareLinkText>Share link</ShareLinkText>
                        </ShareLinkLabel>
                        {isFetchingLink || isTogglingLink ? (
                            isFetchingLink ? (
                                <ShareLinkSkeleton variant="rectangular" />
                            ) : (
                                <CircularProgress size={24} />
                            )
                        ) : (
                            <Switch checked={isShareLinkEnabled} onChange={handleToggleShareLink} disabled={!selectedAgent} />
                        )}
                    </ShareLinkHeader>
                    {isShareLinkEnabled && shareUrl && (
                        <LinkInputContainer>
                            <StyledTextField value={shareUrl} InputProps={{ readOnly: true }} fullWidth size="small" />
                            <CopyButton ariaLabel="copy link" onClick={handleCopyLink}>
                                <MaskedIcon src={CopyIcon} size="20px" color="white" />
                                <CopyButtonText>Copy</CopyButtonText>
                            </CopyButton>
                        </LinkInputContainer>
                    )}
                </ShareLinkSection>
            </Content>
        </StyledDialog>
    );
};
